import { useMemo } from 'react';

import SubActivityPrint from '../../sub-activity/sub-activity-print';
import {
  RESPONSE_FREE,
  ViewProps,
} from './interactive-response-categorise.constants';
import { initialise } from './play/interactive-response-categorise-play.utils';
import {
  InteractiveResponseTitle,
  InteractiveResponseViewScaler,
} from '../shared/interactive-response-shared';
import { InteractiveResponseContainer } from '../interactive-response.style';
import InteractiveDragDisplay from '../../shared/interactive-drag-drop/interactive-drag-display';
import { ComponentAspectRatio } from '../../shared/component-height/component-dimensions';
import { ComponentAbsoluteWrapper } from '../../shared/component-height/component-dimensions.style';
import { useContainerDimensions } from '../../shared/component-height/component-dimensions.hooks';
import { defaultDimensions } from '../interactive-response-shared.constants';
import { getShouldShowNumberOfItemsHint } from './settings/interactive-response-categorise.utils';
import { InteractiveResponsePreviewContainer } from '../shared/interactive-response-shared.style';
import { InteractiveCategoriseDropItemTarget } from './play/interactive-response-categorise-drop.style';
import { InteractiveDropDisplay } from './play/interactive-response-categorise-drop-display';
import * as Styled from './play/interactive-response-categorise.style';

const InteractiveResponseCategorisePreview = ({
  properties,
  subActivityProperties,
}: ViewProps) => {
  const isShowNumberOfItemsHint = getShouldShowNumberOfItemsHint(
    properties.interactiveResponseCategorise.showNumberOfItemsHint
  );
  const { dragItems, dropItems } = useMemo(() => {
    return initialise({
      items: properties.interactiveResponseCategorise.items,
      categories: properties.interactiveResponseCategorise.categories,
      showAnswer:
        properties.interactiveResponseCategorise.answerType !== RESPONSE_FREE,
      isShowNumberOfItemsHint,
      shouldShuffle: true,
      variant: properties.interactiveResponseCategorise.variant,
    });
  }, [
    properties.interactiveResponseCategorise.items,
    properties.interactiveResponseCategorise.categories,
    properties.interactiveResponseCategorise.answerType,
    properties.interactiveResponseCategorise.variant,
    isShowNumberOfItemsHint,
  ]);

  const { setContainerElement } = useContainerDimensions({
    aspectRatio: 16 / 9,
  });

  return (
    <SubActivityPrint subActivityDetails={subActivityProperties}>
      <InteractiveResponseContainer>
        <ComponentAspectRatio
          dimensions={properties.categoriseDimensionData || defaultDimensions}
        >
          <ComponentAbsoluteWrapper ref={setContainerElement}>
            <InteractiveResponseViewScaler
              key={properties.interactiveResponseCategorise.answerType}
            >
              <InteractiveResponseTitle
                title={properties.interactiveResponseCategorise.title}
                subTitle={properties.interactiveResponseCategorise.subTitle}
              />

              <InteractiveResponsePreviewContainer>
                {dragItems.map(dragItem => (
                  <Styled.InteractiveResponseCategoryContainerItem
                    key={dragItem.id}
                  >
                    <InteractiveDragDisplay
                      imageUrl={dragItem.question.image?.small}
                      isFit={dragItem.question.image?.isFit}
                      description={dragItem.question.image?.description || ''}
                      baseColour={dragItem.question.baseColour}
                      variant={dragItem.question.variant}
                      text={dragItem.question.text}
                      richtext={dragItem.question.richtext}
                    />
                  </Styled.InteractiveResponseCategoryContainerItem>
                ))}
              </InteractiveResponsePreviewContainer>

              <Styled.InteractiveResponseCategories>
                {dropItems.map(dropItem => {
                  return (
                    <Styled.InteractiveResponseCategory key={dropItem.id}>
                      <Styled.InteractiveResponseCategoryLabel>
                        {dropItem.label}
                      </Styled.InteractiveResponseCategoryLabel>
                      <InteractiveCategoriseDropItemTarget>
                        {isShowNumberOfItemsHint && (
                          <>
                            {dropItem.zones.map((zone, index) => {
                              return (
                                <InteractiveDropDisplay
                                  key={index}
                                  hasDropped={false}
                                  isContinuousMarking={false}
                                >
                                  <div />
                                </InteractiveDropDisplay>
                              );
                            })}
                          </>
                        )}
                      </InteractiveCategoriseDropItemTarget>
                    </Styled.InteractiveResponseCategory>
                  );
                })}
              </Styled.InteractiveResponseCategories>
            </InteractiveResponseViewScaler>
          </ComponentAbsoluteWrapper>
        </ComponentAspectRatio>
      </InteractiveResponseContainer>
    </SubActivityPrint>
  );
};

export default InteractiveResponseCategorisePreview;
